import Phaser from "phaser";
import { BOARD_TEXTURES } from "../assets";
import { ELEMENTS } from "../model/balance";
import type { Element } from "../model/board";
import { createRun } from "../model/run";
import { loadRun, saveRun } from "../persistence";
import { setGameStatus } from "../status";
import { text } from "../view/BattlefieldView";
import { BattleButton, PictureButton, PortraitCard } from "../view/Controls";
import { motionEnabled } from "../view/preferences";
import { gameAudio } from "../view/Audio";
import { STAGE } from "../view/stage";
import { requestLeaveMatch } from "../view/OnlineOverlay";
import { multiplayer } from "../multiplayer";

export class StartScene extends Phaser.Scene {
  private cards: PortraitCard[] = [];
  private choosing = false;
  private online = false;
  private menu = false;
  constructor() { super("StartScene"); }
  init(data?: { online?: boolean; menu?: boolean }): void { this.online = Boolean(data?.online); this.menu = Boolean(data?.menu); }
  create(): void {
    this.cards = []; this.choosing = false;
    if (!this.online && !this.menu && multiplayer.resume()) { this.scene.start("LobbyScene"); return; }
    const saved = this.online || this.menu ? null : loadRun();
    if (saved) {
      this.scene.start(saved.phase === "battle" ? "BattleScene" : "BoardScene", { run: saved });
      return;
    }
    this.add.image(STAGE.width / 2, STAGE.height / 2, BOARD_TEXTURES.backdrop).setDisplaySize(STAGE.width, STAGE.height);
    this.add.rectangle(STAGE.width / 2, STAGE.height / 2, STAGE.width, STAGE.height, 0x142e32, 0.48);
    text(this, 768, 205, "MERGEBORN", 82).setOrigin(0.5).setShadow(0, 5, "#193a33", 10);
    ELEMENTS.forEach((element, i) => {
      this.cards.push(new PortraitCard(this, 420 + i * 348, 550, { element, tier: 1 },
        () => this.choose(element), 280, 340, i * 85));
    });
    if (this.online) new PictureButton(this, 1470, 66, "exit", "Leave multiplayer match", requestLeaveMatch);
    if (!this.online) {
      new BattleButton(this, 768, 842, () => this.joinMultiplayer(), "Multiplayer", "team");
      this.add.text(STAGE.width / 2, 985, "Play together", { fontFamily: "Trebuchet MS, sans-serif", fontSize: "27px", color: "#fff0cf" }).setOrigin(0.5);
    }
    const chooseKey = (event: KeyboardEvent) => {
      // Native focused picture buttons handle Enter themselves.
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && (focused.isContentEditable || focused.matches("input, textarea, select, button"))) return;
      const key = event.key.toLowerCase();
      const element = key === "1" || key === "f" || key === "enter" ? "fire" :
        key === "2" || key === "i" ? "ice" : key === "3" || key === "l" ? "lightning" : null;
      if (element) this.choose(element);
    };
    this.input.keyboard?.on("keydown", chooseKey);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.input.keyboard?.off("keydown", chooseKey));
    setGameStatus("ready", "Choose Fire, Ice, or Lightning to begin.");
  }
  update(time: number): void { this.cards.forEach(card => card.update(time)); }
  private choose(element: Element): void {
    if (this.choosing) return;
    this.choosing = true;
    gameAudio.unlock(); gameAudio.selection();
    this.cards.forEach(card => card.disable());
    if (this.online) {
      void multiplayer.choose(element).then(match => {
        this.scene.start("BoardScene", { run: match.own.run!, online: true });
      }).catch(error => {
        this.choosing = false; this.cards.forEach(card => card.setEnabled(true)); multiplayer.fail(error.message);
      });
      return;
    }
    const run = createRun(element);
    saveRun(run);
    const card = this.cards[ELEMENTS.indexOf(element)];
    if (!motionEnabled()) { this.scene.start("BattleScene", { run }); return; }
    this.cards.filter(other => other !== card).forEach(other => this.tweens.add({ targets: other.root, alpha: 0.25, duration: 180 }));
    this.tweens.killTweensOf(card.root);
    this.tweens.add({ targets: card.root, scale: 1.12, y: card.root.y - 22, duration: 240, ease: "Back.Out",
      onComplete: () => this.scene.start("BattleScene", { run }) });
  }
  private joinMultiplayer(): void {
    if (this.choosing) return;
    this.choosing = true; this.cards.forEach(card => card.disable());
    this.scene.start("LobbyScene", { enterName: true });
  }
}
